import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useHasPermission } from '../hooks/useHasPermission';
import { CommandService } from '../services';
import { DeviceCommand, SearchCommandsRequest, SearchCommandsResponse } from '../interfaces/command';
import { parseCommandSchema } from '../utils/commandSchema';
import { hash, humanizeEnum } from '../utils';

export const useCommandsTemplatesQuery = () => {
  const queryKey = ['commands-key'];
  const canView = useHasPermission('commands:read:templates');

  const queryFn = () => CommandService.getTemplates();

  const queryResult = useQuery({
    queryKey,
    queryFn,
    enabled: canView,
  });

  // Parse command schema when data is available
  const parsedSchema = useMemo(() => {
    if (!queryResult.data || Object.keys(queryResult.data).length === 0) {
      return null;
    }
    return parseCommandSchema(queryResult.data);
  }, [queryResult.data]);

  // Extract command types for filters
  const commandTypes = useMemo(() => {
    if (!parsedSchema) {
      return [];
    }
    return parsedSchema.commandDefinitions.map((cmd) => ({
      id: cmd.id.toUpperCase(),
      label: humanizeEnum(cmd.id),
    }));
  }, [parsedSchema]);

  return {
    ...queryResult,
    commandSchemas: parsedSchema?.commandSchemas || {},
    commandDefinitions: parsedSchema?.commandDefinitions || [],
    commandTypes,
  };
};

export const useDeviceCommandsQuery = (deviceId?: string) => {
  const canView = useHasPermission('commands:read');
  return useQuery<{ commands: DeviceCommand[] }>(
    ['device-commands', deviceId],
    () => (deviceId ? CommandService.getDeviceCommands(deviceId) : Promise.resolve({ commands: [] })),
    {
      enabled: !!deviceId && canView,
    },
  );
};

export const useSearchCommandsQuery = (filters: SearchCommandsRequest, size: number, page: number) => {
  const canView = useHasPermission('commands:read');

  const filtersHash = hash(filters);

  const queryResult = useQuery<SearchCommandsResponse>({
    queryKey: ['commands-search', filtersHash, size, page],
    queryFn: () => CommandService.searchCommands(filters, size, page),
    enabled: canView,
  });

  // Accumulate all loaded traces from React Query cache
  const allTraces = useMemo(() => {
    const traces: any[] = [];

    // Iterate through all pages loaded so far
    for (let p = 1; p <= page; p++) {
      const cachedData = queryClient.getQueryData<SearchCommandsResponse>(['commands-search', filtersHash, size, p]);
      if (cachedData?.items) {
        traces.push(...cachedData.items);
      }
    }

    return traces;
  }, [page, filtersHash, size, queryResult.data]);

  return {
    ...queryResult,
    allTraces,
  };
};

export const forceRefreshCommands = () => {
  queryClient.invalidateQueries(['commands-key']);
};

export const forceRefreshDeviceCommands = (deviceId: string) => {
  queryClient.invalidateQueries(['device-commands', deviceId]);
};

export const forceRefreshSearchCommands = () => {
  queryClient.invalidateQueries(['commands-search']);
};
